import React from 'react';

import { Settings as SettingsIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

interface PreferencesDrawerProps {
  open: boolean;
  onClose: () => void;
  onChooseColumns: () => void;
  onResetColumns: () => void;
  themeColor: string;
  onThemeColorChange: (color: string) => void;
  showTooltips: boolean;
  onTooltipToggle: (enabled: boolean) => void;
}

const THEME_COLORS = [
  { name: 'Blue', value: '#1976d2', hex: '#1976d2' },
  { name: 'Purple', value: '#9c27b0', hex: '#9c27b0' },
  { name: 'Green', value: '#2e7d32', hex: '#2e7d32' },
  { name: 'Orange', value: '#ed6c02', hex: '#ed6c02' },
  { name: 'Pink', value: '#d81b60', hex: '#d81b60' },
  { name: 'Teal', value: '#00897b', hex: '#00897b' },
  { name: 'Indigo', value: '#3949ab', hex: '#3949ab' },
  { name: 'Red', value: '#d32f2f', hex: '#d32f2f' },
];

const PreferencesDrawer: React.FC<PreferencesDrawerProps> = ({
  open,
  onClose,
  onChooseColumns,
  onResetColumns,
  themeColor,
  onThemeColorChange,
  showTooltips,
  onTooltipToggle,
}) => {
  const theme = useTheme();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          bgcolor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(40px) saturate(180%)',
          borderLeft: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <SettingsIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={700}>
              Preferences
            </Typography>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              '&:hover': {
                bgcolor: alpha(themeColor, 0.1),
                borderColor: themeColor,
              },
            }}
          >
            <Typography variant="body1">✕</Typography>
          </IconButton>
        </Stack>

        <Stack spacing={4}>
          {/* Theme Color */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Theme Color
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a color for the primary theme accent
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1.5,
              }}
            >
              {THEME_COLORS.map((color) => (
                <Box
                  key={color.value}
                  onClick={() => onThemeColorChange(color.value)}
                  sx={{
                    aspectRatio: '1',
                    borderRadius: 3,
                    bgcolor: color.hex,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    border: themeColor === color.value ? `3px solid ${color.hex}` : `3px solid transparent`,
                    boxShadow:
                      themeColor === color.value
                        ? `0 0 0 2px ${theme.palette.background.paper}, 0 4px 12px ${alpha(color.hex, 0.4)}`
                        : `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
                    transform: themeColor === color.value ? 'scale(1.1)' : 'scale(1)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: `0 4px 16px ${alpha(color.hex, 0.3)}`,
                    },
                    '&::after':
                      themeColor === color.value
                        ? {
                            content: '"✓"',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: theme.palette.common.white,
                            fontSize: theme.typography.h5.fontSize,
                            fontWeight: theme.typography.fontWeightBold,
                            textShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.3)}`,
                          }
                        : {},
                  }}
                />
              ))}
            </Box>
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(themeColor, 0.1),
                border: `1px solid ${alpha(themeColor, 0.3)}`,
              }}
            >
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                Selected: {THEME_COLORS.find((c) => c.value === themeColor)?.name || 'Blue'}
              </Typography>
            </Box>
          </Box>

          {/* Column Visibility */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Column visibility
            </Typography>
            <Stack spacing={1.5}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  onChooseColumns();
                  onClose();
                }}
                sx={{
                  justifyContent: 'flex-start',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Choose columns
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  onResetColumns();
                  onClose();
                }}
                sx={{
                  justifyContent: 'flex-start',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Reset to defaults
              </Button>
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Tooltips
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={showTooltips}
                    onChange={(e) => onTooltipToggle(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: themeColor,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: themeColor,
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Show Tooltips
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {showTooltips ? 'Tooltips are enabled' : 'Tooltips are disabled'}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
            </Box>
          </Box>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default PreferencesDrawer;
